// WxCC Operations Center — dashboard on top of the Cisco Agentic MCP
// servers (Contact Center Operation + Webex Contact Center). The entry
// point is a mode picker; the heavy lifting lives in ./wxccAiOps/*.
//
// Modes:
//   calls       → Call search → CallDetail (unified, interactionId-keyed)
//   analytics   → Session activity snapshot (volume / outcomes / top agents)
//   ai-agents   → AI-Agent-first drill-down (list → sessions → session detail)
//   tools       → MCP tools inventory (debug)

import React, { useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  Code,
  Container,
  Grid,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconArrowLeft,
  IconBook,
  IconChartBar,
  IconPhone,
  IconRobot,
  IconSparkles,
  IconTools,
} from '@tabler/icons-react';

import { useAuth } from '../../../shared/contexts/AuthContext';
import { listTools, setMcpAuthTokenGetter } from '../api_scripts/wxccMcpClient';
import { CallSearch } from './wxccAiOps/CallSearch';
import { CallDetail } from './wxccAiOps/CallDetail';
import { Analytics } from './wxccAiOps/Analytics';
import {
  AgentTroubleshooting,
  SessionDetail,
  SessionList,
} from './wxccAiOps/AgentTroubleshooting';
import { AgentTuning } from './wxccAiOps/AgentTuning';
import { BotCatalog } from './wxccAiOps/BotCatalog';

// Mode picker cards.
const MODES = [
  {
    key: 'calls',
    title: 'Call search',
    icon: IconPhone,
    server: 'OPS',
    blurb: 'Find a call by ANI + date window. Row click opens the unified detail view (CC + AI Agent sides joined on interactionId).',
    status: 'live',
    tools: ['run-search-query → contactAIAgentSession'],
  },
  {
    key: 'analytics',
    title: 'Analytics',
    icon: IconChartBar,
    server: 'OPS',
    blurb: 'Outcome mix, daily volume, escalation / abandonment rates, and the agents / entry points driving them. One-screen session-activity snapshot.',
    status: 'live',
    tools: ['run-search-query → contactAIAgentSession'],
  },
  {
    key: 'ai-agents',
    title: 'AI Agent troubleshooting',
    icon: IconRobot,
    server: 'OPS + CC',
    blurb: 'Recent sessions per agent, turn-by-turn detail, tool invocations, contact timeline join, audit findings, Bedrock AI summary.',
    status: 'live',
    tools: ['list-ai-agents', 'list-ai-agent-sessions', 'get-ai-agent-session', 'get-ai-agent-session-messages', 'audit-ai-agent', 'get-contact-timeline'],
  },
  {
    key: 'catalog',
    title: 'Bot Catalog',
    icon: IconBook,
    server: 'static',
    blurb: 'Spec view of this tenant\'s AI Agent build files — tool catalog, declared custom-event exits, prompt excerpts, intent surface. Pairs with the Spec vs Reality report under Analytics → Technical.',
    status: 'live',
    tools: ['data/botCatalog.json (static)'],
  },
  {
    key: 'tools',
    title: 'MCP tools inventory',
    icon: IconTools,
    server: 'OPS + CC',
    blurb: 'Enumerate every tool each MCP server exposes. Useful for discovery and sanity checking.',
    status: 'live',
    tools: ['tools/list'],
  },
];

export default function WxccAiOps({ onBackToHome }) {
  const { getApiToken } = useAuth();
  // Register the MSAL token-getter so wxccMcpClient can attach a Bearer
  // when it's running in Lambda-proxy mode (deployed builds).
  useEffect(() => {
    setMcpAuthTokenGetter(getApiToken);
    return () => setMcpAuthTokenGetter(null);
  }, [getApiToken]);

  const [view, setView] = useState(() => {
    if (typeof window === 'undefined') return { name: 'home' };
    const params = new URLSearchParams(window.location.search);
    const interactionId = params.get('interactionId');
    if (!interactionId) return { name: 'home' };
    const twelveHours = 12 * 60 * 60 * 1000;
    const now = Date.now();
    return {
      name: 'call-detail',
      interactionId,
      window: { from: now - twelveHours, to: now + twelveHours },
      cachedAiSessions: null,
    };
  });

  return (
    <Container size="xl" py="lg">
      <Group justify="space-between" mb="lg">
        <Group gap="sm">
          <IconSparkles size={30} color="#1976d2" />
          <Box>
            <Title order={2}>WxCC Operations Center</Title>
            <Text size="sm" c="dimmed">
              Cisco Agentic MCP dashboard — call search, analytics, and AI Agent troubleshooting.
            </Text>
          </Box>
        </Group>
        <Group gap="xs">
          {view.name !== 'home' && (
            <Button
              size="xs"
              variant="light"
              leftSection={<IconArrowLeft size={14} />}
              onClick={() => setView({ name: 'home' })}
            >
              Modes
            </Button>
          )}
          {onBackToHome && (
            <Button size="xs" variant="subtle" onClick={onBackToHome}>
              Back to Cisco Home
            </Button>
          )}
        </Group>
      </Group>

      {view.name === 'home' && <ModeGrid onPick={(mode) => setView({ name: mode })} />}
      {view.name === 'calls' && <CallSearch setView={setView} />}
      {view.name === 'call-detail' && (
        <CallDetail
          interactionId={view.interactionId}
          window={view.window}
          cachedAiSessions={view.cachedAiSessions}
          setView={setView}
        />
      )}
      {view.name === 'analytics' && <Analytics setView={setView} />}
      {view.name === 'ai-agents' && <AgentTroubleshooting setView={setView} />}
      {view.name === 'sessions' && (
        <SessionList agentId={view.agentId} agentName={view.agentName} setView={setView} />
      )}
      {view.name === 'session' && (
        <SessionDetail agentId={view.agentId} agentName={view.agentName} sessionId={view.sessionId} setView={setView} />
      )}
      {view.name === 'agent-tuning' && (
        <AgentTuning agentId={view.agentId} agentName={view.agentName} setView={setView} />
      )}
      {view.name === 'catalog' && <BotCatalog />}
      {view.name === 'tools' && <ToolsInventory />}
    </Container>
  );
}

// ─── Mode picker ────────────────────────────────────────────────────────

function ModeGrid({ onPick }) {
  return (
    <Grid gutter="md">
      {MODES.map((m) => {
        const Icon = m.icon;
        return (
          <Grid.Col key={m.key} span={{ base: 12, sm: 6, md: 4 }}>
            <Paper
              withBorder
              p="md"
              radius="md"
              onClick={() => onPick(m.key)}
              style={{ cursor: 'pointer', height: '100%' }}
            >
              <Group gap="xs" mb="xs">
                <Icon size={20} color="#1976d2" />
                <Title order={5} style={{ flex: 1 }}>
                  {m.title}
                </Title>
                <Badge size="xs" color={m.server.includes('+') ? 'teal' : m.server === 'OPS' ? 'blue' : 'grape'}>
                  {m.server}
                </Badge>
                {m.status === 'planned' && (
                  <Badge size="xs" color="yellow" variant="light">
                    planned
                  </Badge>
                )}
              </Group>
              <Text size="sm" c="dimmed" mb="sm">
                {m.blurb}
              </Text>
              <Group gap={4}>
                {m.tools.map((t) => (
                  <Code key={t} c="dimmed" style={{ fontSize: 10 }}>
                    {t}
                  </Code>
                ))}
              </Group>
            </Paper>
          </Grid.Col>
        );
      })}
    </Grid>
  );
}

// ─── Tools inventory (debug) ────────────────────────────────────────────

function ToolsInventory() {
  const [inv, setInv] = useState({ ops: null, cc: null, loading: true });

  useEffect(() => {
    let cancelled = false;
    Promise.allSettled([listTools('ops'), listTools('cc')]).then(([opsR, ccR]) => {
      if (cancelled) return;
      setInv({
        ops: opsR.status === 'fulfilled' ? { data: opsR.value } : { error: opsR.reason?.message || String(opsR.reason) },
        cc: ccR.status === 'fulfilled' ? { data: ccR.value } : { error: ccR.reason?.message || String(ccR.reason) },
        loading: false,
      });
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Stack>
      <Title order={3}>MCP tools inventory</Title>
      <Text size="sm" c="dimmed">
        <Code>tools/list</Code> from both endpoints.
      </Text>
      {inv.loading && <Loader size="sm" />}
      {!inv.loading &&
        [['OPS', 'ops'], ['CC', 'cc']].map(([label, key]) => (
          <EndpointToolsBlock key={key} label={label} inv={inv[key]} />
        ))}
    </Stack>
  );
}

function EndpointToolsBlock({ label, inv }) {
  if (inv.error)
    return (
      <Alert color="red" icon={<IconAlertTriangle size={16} />} title={`${label} — enumeration failed`}>
        <Text size="xs">{inv.error}</Text>
      </Alert>
    );
  const tools = inv.data?.tools || [];
  return (
    <Paper withBorder p="md" radius="md">
      <Group justify="space-between" mb="xs">
        <Title order={5}>{label}</Title>
        <Badge>{tools.length} tools</Badge>
      </Group>
      <Stack gap="xs">
        {tools.map((t) => (
          <div key={t.name}>
            <Text size="sm" fw={500}>
              <Code>{t.name}</Code> {t.title ? `— ${t.title}` : ''}
            </Text>
            {t.description && (
              <Text size="xs" c="dimmed" lineClamp={3}>
                {t.description}
              </Text>
            )}
          </div>
        ))}
      </Stack>
    </Paper>
  );
}
